import React, { type ComponentProps } from 'react';

import { integerToCurrency } from 'loot-core/src/shared/util';

import { theme } from '../../style';
import { SidebarGroup } from '../budget/SidebarGroup';
import { View } from '../common/View';
import {
  type DragState,
  DropHighlight,
  type OnDragChangeCallback,
  type OnDropCallback,
  useDraggable,
  useDroppable,
} from '../sort';
import { useFormat } from '../spreadsheet/useFormat';
import { Field, Row, ROW_HEIGHT } from '../table';

import {
  type CellEditing,
  type ITableTotals,
  type VisibleTotals,
} from './types';
import { VIEW_MODE } from './util';

type IncomeGroupProps = {
  year: number;
  group: {
    id: string;
    hidden: number;
    categories: any[];
    is_income: number;
    is_donation: number;
    name: string;
    sort_order: number;
    tombstone: number;
  };
  groupType: string;
  months: { index: number; month: string }[];
  groupTotals: ITableTotals;
  visibleTotals: VisibleTotals;
  editingCell: CellEditing;
  collapsed: boolean;
  dragState: DragState<ComponentProps<typeof SidebarGroup>['group']>;
  onEditName: (id: string) => void;
  onSave: (group: object) => Promise<void>;
  onDelete?: ComponentProps<typeof SidebarGroup>['onDelete'];
  onDragChange: OnDragChangeCallback<
    ComponentProps<typeof SidebarGroup>['group']
  >;
  onReorderGroup: OnDropCallback;
  onReorderCategory: OnDropCallback;
  onToggleCollapse: (id: string) => void;
  onShowNewCategory: (groupId: string) => void;
  viewMode: VIEW_MODE;
};

function Group({
  year,
  group,
  groupType,
  months,
  groupTotals,
  visibleTotals,
  viewMode,
  editingCell,
  collapsed,
  dragState,
  onEditName,
  onSave,
  onDelete,
  onDragChange,
  onReorderGroup,
  onReorderCategory,
  onToggleCollapse,
  onShowNewCategory,
}: IncomeGroupProps) {
  const format = useFormat();
  const groupBudgetByMonth = visibleTotals.groupMonthBudget.get(group.id);
  const groupBudgetYear = visibleTotals.groupYearBudget.get(group.id);

  const dragging = dragState && dragState.item === group;

  const { dragRef } = useDraggable({
    type: 'group',
    onDragChange,
    item: group,
    canDrag: !group.is_donation && editingCell === null,
  });

  const { dropRef, dropPos } = useDroppable({
    types: 'group',
    id: group.id,
    canDrop: dropPos => !(group.is_donation && dropPos === 'top' || group.is_income),
    onDrop: onReorderGroup,
  });

  // Empty group case (most likely)
  const { dropRef: catDropRef, dropPos: catDropPos } = useDroppable({
    types: 'expense-category', // Since there are no more than 1 income groups, this is not needed
    id: group.id,
    onDrop: onReorderCategory,
    onLongHover: () => {
      if (collapsed) {
        onToggleCollapse(group.id);
      }
    },
    canDrop: () => !(group.is_donation && catDropPos === 'top' || group.is_income)
  });

  return (
    <Row
      data-adoption-id={'group-' + groupType}
      collapsed={true}
      style={{
        fontWeight: 600,
        opacity: group.hidden ? 0.33 : undefined,
      }}
    >
      {dragState &&
        !dragState.preview &&
        dragState.type === 'group' &&
        !(group.is_donation && dropPos === 'top' || group.is_income) && (
          <View
            innerRef={dropRef}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              height: collapsed
                ? ROW_HEIGHT - 1
                : (1 + group.categories.length) * (ROW_HEIGHT - 1) + 1,
              zIndex: 10000,
            }}
          >
            <DropHighlight pos={dropPos} offset={{ top: 1 }} />
          </View>
        )}
      {dragState &&
        !dragState.preview &&
        dragState.type === 'expense-category' &&
        !(group.is_donation && catDropPos === 'top' || group.is_income) && (
          <DropHighlight pos={catDropPos} offset={{ top: 1 }} />
        )}
      <View
        innerRef={catDropRef}
        style={{
          flex: 1,
          flexDirection: 'row',
          minWidth: 'fit-content',
          opacity: dragging && !dragState.preview ? 0.3 : 1,
          backgroundColor: theme.tableRowHeaderBackground,
          zIndex: 1000,
        }}
      >
        <SidebarGroup
          innerRef={dragRef}
          group={group}
          collapsed={collapsed}
          editing={
            editingCell &&
            editingCell.column === 'name' &&
            editingCell.id === group.id
          }
          dragPreview={dragging && dragState.preview}
          onEdit={onEditName}
          onSave={onSave}
          onDelete={onDelete}
          onToggleCollapse={onToggleCollapse}
          onShowNewCategory={onShowNewCategory}
          style={{
            position: 'sticky',
            left: 0,
            zIndex: 1000,
          }}
        />
        {viewMode === VIEW_MODE.ONLY_MONTHS ? null : (
          <View
            style={{
              flexDirection: 'row',
              position: 'sticky',
              left: 200,
              backgroundColor: 'var(--color-tableRowHeaderBackground)',
              zIndex: 1000,
            }}
          >
            <Field width="120" name="frequency" />
            <Field width="120" name="amount" />
            <Field width="120" name="monthly" style={{ textAlign: 'right' }}>
              {integerToCurrency(groupTotals?.monthly || 0)}
            </Field>
            <Field width="120" name="yearly" style={{ textAlign: 'right' }}>
              {integerToCurrency(groupTotals?.yearly || 0)}
            </Field>
            <View style={{ width: '60px' }} />
          </View>
        )}
        <View
          style={{
            flexDirection: 'row',
            flex: 1,
            minWidth: 'fit-content',
            alignItems: 'stretch',
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
        >
          <View
            style={{
              flex: 1,
              justifyContent: 'center',
              textAlign: 'right',
              minWidth: '100px',
              backgroundColor: theme.tableRowHeaderBackground,
              padding: '0 3px',
            }}
          >
            <View
              style={{ color: theme.pageTextLight, fontWeight: 600 }}
            >
              {format(
                Math.round(groupBudgetYear?.monthlyAvg ?? 0),
                'financial',
              )}
            </View>
          </View>
          <View
            style={{
              flex: 1,
              justifyContent: 'center',
              textAlign: 'right',
              minWidth: '100px',
              backgroundColor: theme.tableRowHeaderBackground,
              padding: '0 3px',
            }}
          >
            <View
              style={{ color: theme.pageTextLight, fontWeight: 600 }}
            >
              {format(groupBudgetYear?.total ?? 0, 'financial')}
            </View>
          </View>
          {months.map(o => (
            <View
              key={o.index}
              style={{
                flex: 1,
                justifyContent: 'center',
                textAlign: 'right',
                minWidth: '100px',
                backgroundColor: 'var(--color-tableRowHeaderBackground)',
                padding: '0 3px',
              }}
            >
              <View
                style={{ color: theme.pageTextLight, fontWeight: 600 }}
              >
                {format(
                  groupBudgetByMonth?.get(o.month) ?? 0,
                  'financial',
                )}
              </View>
            </View>
          ))}
        </View>
      </View>
    </Row>
  );
}

export default Group;
